import {
  Box,
  Card,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import Page from "../Page";
import { useState } from "react";
import downloadFill from "@iconify/icons-eva/download-fill";
import redirectFill from "@iconify/icons-eva/arrow-back-fill";
import arrowLeftFill from "@iconify/icons-eva/arrow-left-fill";
import html2pdf from "html2pdf.js";
import { chargerTransactionActivityUrl } from "../../react-query/endpoints";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { is } from "../../util/is";
import { convertInvoiceTimestampToLocal } from "../../util/timezoneUtils";
import { Icon } from "@iconify/react";
import { LoadingButton } from "@mui/lab";
import Constant from "../../util/constants";
import { useDomainConfig } from "../../hooks/useDomainConfig";

const { chargeConnectDisconnectMsg } = Constant;

const StyledContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  paddingTop: theme.spacing(0),
  paddingBottom: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(0),
  },
}));

export const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  fontSize: theme.spacing(3),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "16px",
  boxShadow: "0 4px 24px 0 rgba(0,0,0,0.06)",
  border: `1px solid rgba(145, 158, 171, 0.16)`,
  background: `linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0) 100%)`,
  position: "relative",
  overflow: "hidden",
  width: "100%",
  margin: "0 auto",
  maxWidth: "800px",
  "@media (max-width: 900px)": {
    padding: theme.spacing(2.5),
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `16px 16px 0 0`,
  },
}));

const fetchInvoiceDetails = async (endPoint, activitytoken) => {
  const response = await axios.get(chargerTransactionActivityUrl(endPoint), {
    headers: {
      authorization: `Bearer ${activitytoken}`,
    },
  });
  return response.data;
};

const TransactionInvoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const domainConfig = useDomainConfig();

  const formatDateTime = (dateTimeStamp, dateFormat) => {
    // console.log(`[Invoice] Formatting date time - Input: ${dateTimeStamp}, Format: ${dateFormat}, Domain: ${domainConfig?.domain}`);
    
    // Use the invoice-specific timezone conversion for better handling
    const result = convertInvoiceTimestampToLocal(dateTimeStamp, dateFormat);
    // console.log(`[Invoice] Formatted result: ${result}`);
    return result;
  };

  const [receiptObj, setReceiptObj] = useState({
    receiptNo: "",
    receiptDate: "",
    stationId: "",
    stationAddress: "",
    chargingDuration: "",
    currency: "",
    energyConsumption: "",
    timeConsumption: "",
    priceType: "",
    totalCostExcludingTax: "",
    taxPercent: "",
    taxAmount: "",
    transactionPercent: "",
    transactionFee: "",
    totalCostIncludingTaxAndTransactionFee: "",
    email: "",
    phone: "",
    chargeBoxId: "",
    domain: "",
  });
  const [contactMail, setContactMail] = useState("info@amiktech.in");

  const onFetchingInvoiceDetails = (data) => {
    const { invoiceData } = data;
    if (!is.empty(invoiceData)) {
      const {
        chargeBoxAddress,
        invoiceId,
        priceType,
        totalCostExcludingTax,
        totalCostIncludingTaxAndTransactionFee,
        taxPercent,
        taxAmount,
        transactionFee,
        currency,
        chargeBox,
        energyConsumption,
        timeConsumption,
        starttime,
        stoptime,
        refundAmount,
        totalAmountPaid,
        email,
        phone,
        chargeBoxId,
        domain,
      } = invoiceData;
      const updatedReceiptData = {
        receiptNo: invoiceId,
        receiptDate: `${formatDateTime(stoptime, "yyyy-MM-dd 'at' HH:mm")}`,
        chargingDuration: `${formatDateTime(
          starttime,
          "yyyy-MM-dd HH:mm"
        )} - ${formatDateTime(stoptime, "HH:mm")}`,
        priceType,
        stationId: chargeBox,
        stationAddress: chargeBoxAddress,
        currency,
        energyConsumption,
        timeConsumption,
        totalCostExcludingTax,
        taxPercent,
        taxAmount,
        transactionFee,
        totalCostIncludingTaxAndTransactionFee,
        refund: refundAmount,
        totalAmountPaid,
        email,
        phone,
        chargeBoxId,
        domain,
      };
      if (updatedReceiptData.domain.toLowerCase() === "moveonev") {
        setContactMail("info@moveonev.com");
      } else if (updatedReceiptData.domain.toLowerCase() === "chargemiles") {
        setContactMail("info@chargemiles.ca");
      } else if (updatedReceiptData.domain.toLowerCase() === "acsenergy"){
        setContactMail("support@acsenergy.in");
      }else if (updatedReceiptData.domain.toLowerCase() === "zerofootprint") {
        setContactMail("support@zerofootprint.in");
      }else if (updatedReceiptData.domain.toLowerCase() === "bharatnp") {
        setContactMail("Supgbt@gmail.com");
      }else {
        setContactMail("info@amiktech.in");
      }
      setReceiptObj(updatedReceiptData);
    }
    return;
  };

  const { isLoading } = useQuery(
    ["invoice"],
    () => fetchInvoiceDetails("invoice", token),
    {
      enabled: !is.empty(token),
      onSuccess: onFetchingInvoiceDetails,
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const handlePrintPDF = () => {
    const invoice = document.getElementById("invoice");
    let invoiceClone = invoice.cloneNode(true);
    invoiceClone.style.margin = "20px";
    invoiceClone.style.border = "2px solid #1976D2";
    let invoiceDiv = document.createElement("div");
    invoiceDiv.style.display = "flex";
    invoiceDiv.style.alignItems = "center";
    invoiceDiv.style.justifyContent = "center";
    invoiceDiv.appendChild(invoiceClone);

    html2pdf().from(invoiceDiv).save(`invoice-${receiptObj.receiptNo}.pdf`);
  };

  const redirectToChargerDetails = (charger) => {
    // const charger = invoiceId.split("-")[0];
    navigate(`/charger/${charger}`);
  };

  if (isLoading) {
    return (
      <Page
        title="Invoice | ChargeMiles"
        sx={{ marginTop: { xs: "128px", md: "62px", overflow: "hidden" } }}
      >
        {" "}
        <StyledContainer maxWidth="lg">
          <CircularProgress
            color="success"
            size={40}
            thickness={4}
            variant="indeterminate"
          />{" "}
        </StyledContainer>
      </Page>
    );
  }

  return (
    <Page
      title="Invoice | ChargeMiles"
      sx={{ marginTop: { xs: "128px", md: "62px", overflow: "hidden" } }}
    >
      <StyledContainer maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, width: '100%', maxWidth: '800px' }}>
            <Tooltip title="Back to Details" placement="top">
              <IconButton onClick={() => redirectToChargerDetails(receiptObj.chargeBoxId)} color="primary" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                <Icon icon={arrowLeftFill} width={24} height={24} />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 0.5,
                }}
              >
                Transaction Invoice
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {chargeConnectDisconnectMsg.forDisconnect}
              </Typography>
            </Box>
          </Stack>

        <StyledCard id="invoice">
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Receipt</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '1rem', letterSpacing: 1 }}>
              {receiptObj.receiptNo}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Issued on: {receiptObj.receiptDate}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Station ID</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {receiptObj.stationId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {receiptObj.email ? "Account Email" : "Account Phone"}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                {receiptObj.email || receiptObj.phone || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Station Address</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {receiptObj.stationAddress}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Charging Duration</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {receiptObj.chargingDuration}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{receiptObj.priceType === "min" ? "Time Billed" : "Energy Consumed"}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.main' }}>
                {receiptObj.priceType === "min" ? `${receiptObj.timeConsumption}` : `${receiptObj.energyConsumption}`.replace(/Kw/g, 'kW')}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ borderStyle: 'dashed', my: 3 }} />

          <Stack spacing={2} sx={{ mb: 4 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{receiptObj.currency} {receiptObj.totalCostExcludingTax}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Tax ({receiptObj.taxPercent})</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{receiptObj.currency} {receiptObj.taxAmount}</Typography>
            </Stack>
            {receiptObj.transactionFee && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Transaction Fee</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{receiptObj.currency} {receiptObj.transactionFee}</Typography>
              </Stack>
            )}
            
            <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: '12px', mt: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Total Charging Cost</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                  {receiptObj.currency} {receiptObj.totalCostIncludingTaxAndTransactionFee}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {receiptObj.currency} {receiptObj.totalAmountPaid}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Total Refunded</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  {receiptObj.currency} {receiptObj.refund}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Questions? Reach us at{" "}
              <a href={`mailto:${contactMail}`} style={{ color: '#1976D2', textDecoration: 'none', fontWeight: 600 }}>
                {contactMail}
              </a>
            </Typography>
          </Box>
        </StyledCard>

        <Stack direction="row" justifyContent="center" sx={{ mt: 4, width: '100%' }}>
          <StyledLoadingButton
            size="large"
            type="button"
            variant="contained"
            color="primary"
            startIcon={<Icon icon={downloadFill} color="#ffff" width={24} height={24} />}
            onClick={handlePrintPDF}
            sx={{ px: 4, py: 1.5, borderRadius: '12px', boxShadow: '0 8px 16px 0 rgba(25, 118, 210, 0.24)', background: "linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)", "&:hover": { background: "linear-gradient(135deg, #1565C0 0%, #1976D2 100%)", boxShadow: "0 6px 20px 0 rgba(25, 118, 210, 0.5)" } }}
          >
            Download Digital Invoice
          </StyledLoadingButton>
        </Stack>
      </StyledContainer>
    </Page>
  );
};

export default TransactionInvoice;
